import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import {
    DataGrid,
    GridToolbar,
    GridColDef,
} from "@material-ui/data-grid";
import CancelIcon from "@mui/icons-material/Cancel";
import { makeGETRequestOnUserService, makePOSTRequestOnUserService } from "../../Api/api";
import moment from "moment";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

interface Mandate {
    id: number;
    bank_account_id: number;
    mandate_ref: string | null;
    mandate_token: string | null;
    valid_from: string | null;
    valid_to: string | null;
    mandate_limit: number | null;
    mandate_type: string | null;
    mandate_status: string | null;
    umrn: string | null;
    created_at: string | null;
    received_at: string | null;
    submitted_at: string | null;
    approved_at: string | null;
    cancelled_at: string | null;
    rejected_at: string | null;
    rejected_reason: string | null;
    provider_id: number | null;
    provider_name: string | null;
}

interface MandateDetailsProps {
    bankOldId: string;
}

const MandateDetails = ({ bankOldId }: MandateDetailsProps) => {
    const toastr = require("toastr");
    const [mandates, setMandates] = useState<Mandate[]>([]);
    const [loading, setLoading] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedMandate, setSelectedMandate] =
        useState<Mandate | null>(null);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [totalElements, setTotalElements] = useState(0);

    const statusColors: Record<string, string> = {
        CREATED: "#1976d2",
        RECEIVED: "#323232",
        APPROVED: "#2e7d32",
        SUBMITTED: "#9c27b0",
        REJECTED: "#ed6c02",
        CANCELLED: "#d32f2f",
    };

    const fetchMandates = async () => {
        if (!bankOldId) {
            toastr.error("Bank account ID not found.");
            setMandates([]);
            return;
        }

        try {
            setLoading(true);
            const response = await makeGETRequestOnUserService(
                `/mutual_fund/mf_mandates/list/sync/?bank_account_id=${bankOldId}&page=${page}&size=${pageSize}`
            );

            if (response?.status === 200) {
                const data = response?.data?.data;
                const mandateList = data?.mandates || [];

                setMandates(mandateList);
                setTotalElements(data?.total_elements || 0);

                if (!mandateList.length) {
                    toastr.info("No mandates found.");
                }
            } else {
                toastr.error(
                    response?.data?.message || "Failed to fetch mandates."
                );
                setMandates([]);
                setTotalElements(0);
            }
        } catch (error: any) {
            console.error("Failed to fetch mandates:", error);

            setMandates([]);
            setTotalElements(0);
            toastr.error(
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while fetching mandates."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMandates();
    }, [bankOldId, page, pageSize]);

    const formatDate = (date?: string | null) => {
        if (!date) return "-";
        const parsedDate = moment(date);
        if (!parsedDate.isValid()) {
            return date;
        }
        return parsedDate.format("(HH[H]:mm[M]) MMM DD, YYYY");
    };

    const handleCancelClick = (mandate: Mandate) => {
        setSelectedMandate(mandate);
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        if (cancelLoading) return;
        setCancelDialogOpen(false);
        setSelectedMandate(null);
    };

    const handleConfirmCancel = async () => {
        if (!selectedMandate?.id) {
            toastr.error("Mandate ID not found.");
            return;
        }

        try {
            setCancelLoading(true);
            const response = await makePOSTRequestOnUserService(
                `/mutual_fund/mf_mandates/cancel/${selectedMandate.id}/`
            );

            if (
                response?.status === 200 &&
                response?.data?.success
            ) {
                toastr.success(
                    response?.data?.message ||
                    "Mandate cancelled successfully."
                );

                setCancelDialogOpen(false);
                setSelectedMandate(null);

                await fetchMandates();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Failed to cancel mandate."
                );
            }
        } catch (error: any) {
            console.error("Cancel mandate error:", error);
            toastr.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while cancelling mandate."
            );
        } finally {
            setCancelLoading(false);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Mandate ID",
            width: 180,
        },
        {
            field: "created_at",
            headerName: "Created At",
            width: 220,
        },

        {
            field: "mandate_limit",
            headerName: "Limit",
            width: 150,
        },
        {
            field: "mandate_status",
            headerName: "Status",
            width: 150,
            renderCell: (params: any) => (
                <Typography
                    sx={{
                        color: statusColors[params.value] || "#666",
                    }}
                >
                    {params.value || "-"}
                </Typography>
            ),
        },
        {
            field: "mandate_type",
            headerName: "Type",
            width: 150,
        },
        {
            field: "umrn",
            headerName: "UMRN",
            width: 200,
        },
        {
            field: "valid_from",
            headerName: "Valid From",
            width: 200,
        },
        {
            field: "valid_to",
            headerName: "Valid To",
            width: 200,
        },
        {
            field: "submitted_at",
            headerName: "Submitted At",
            width: 220,
        },
        {
            field: "approved_at",
            headerName: "Approved At",
            width: 220,
        },
        {
            field: "cancelled_at",
            headerName: "Cancelled At",
            width: 220,
        },
        {
            field: "rejected_at",
            headerName: "Rejected At",
            width: 220,
        },
        {
            field: "rejected_reason",
            headerName: "Rejected Reason",
            width: 220,
        },
        {
            field: "cancel",
            headerName: "Cancel",
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params: any) => {
                const isApproved =
                    params.row.mandate_status?.toLowerCase() === "approved";

                return (
                    <HighlightOffIcon
                        style={{
                            cursor: isApproved ? "pointer" : "not-allowed",
                            fontSize: 22,
                            color: isApproved ? "#d32f2f" : "#bdbdbd",
                            opacity: isApproved ? 1 : 0.5,
                        }}
                        onClick={(event) => {
                            event.stopPropagation();

                            if (!isApproved) return;

                            handleCancelClick(params.row.mandate);
                        }}
                    />
                );
            },
        },
    ];

    const rows = mandates.map((mandate) => ({
        id: mandate.id,
        bank_account_id: mandate.bank_account_id,
        mandate_ref: mandate.mandate_ref || "-",
        mandate_type: mandate.mandate_type || "-",
        mandate_limit:
            mandate.mandate_limit !== null &&
                mandate.mandate_limit !== undefined
                ? mandate.mandate_limit
                : "-",
        mandate_status: mandate.mandate_status || "-",
        umrn: mandate.umrn || "-",
        provider_name: mandate.provider_name || "-",
        valid_from: mandate.valid_from
            ? moment(mandate.valid_from).format("MMM DD, YYYY")
            : "-",
        valid_to: mandate.valid_to
            ? moment(mandate.valid_to).format("MMM DD, YYYY")
            : "-",
        created_at: formatDate(mandate.created_at),
        received_at: formatDate(mandate.received_at),
        submitted_at: formatDate(mandate.submitted_at),
        approved_at: formatDate(mandate.approved_at),
        cancelled_at: formatDate(mandate.cancelled_at),
        rejected_at: formatDate(mandate.rejected_at),
        rejected_reason: mandate.rejected_reason || "-",
        mandate: mandate,
    }));

    if (loading) {
        return (
            <Box
                sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: "#262349" }} />
                <Typography color="text.secondary">
                    Loading Mandates...
                </Typography>
            </Box>
        );
    }

    if (!mandates.length && !loading) {
        return (
            <Box
                sx={{
                    height: 300,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography
                    color="text.secondary"
                    sx={{
                        textAlign: "center",
                        fontSize: "16px",
                    }}
                >
                    No mandates found.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Grid className="QuotesDataGrid">
                <Grid style={{ padding: "0 50px" }}>
                    <Grid container alignItems="center" spacing={3}>
                        <Grid item xs>
                            <div style={{ fontSize: "36px", float: "left", margin: "15px 0" }}>Mandate List</div>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            height: "75vh",
                            width: "100%",
                            px: 2,

                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },

                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                            },

                            "& .MuiDataGrid-row:hover": {
                                background:
                                    "#f5f7ff !important",
                            },
                        }}
                    >
                        <DataGrid
                            pagination
                            rows={rows}
                            columns={columns}
                            page={page}
                            pageSize={pageSize}
                            rowCount={totalElements}
                            paginationMode="server"
                            onPageChange={(newPage) => {
                                setPage(newPage);
                            }}
                            onPageSizeChange={(newPageSize) => {
                                setPageSize(newPageSize);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[20, 50, 100]}
                            components={{
                                Toolbar: GridToolbar,
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>
            <Dialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        color: "#262349",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <CancelIcon
                        sx={{
                            color: "#d32f2f",
                            fontSize: 26,
                        }}
                    />
                    Cancel Mandate
                </DialogTitle>

                <DialogContent>
                    <Typography
                        sx={{
                            fontSize: "15px",
                            color: "#444",
                            lineHeight: 1.6,
                        }}
                    >
                        Are you sure you want to cancel this mandate?
                    </Typography>

                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: "#f7f7f9",
                            borderRadius: "6px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                            }}
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: "#777",
                                        mb: 0.5,
                                    }}
                                >
                                    Mandate ID
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "15px",
                                        fontWeight: 600,
                                        color: "#262349",
                                    }}
                                >
                                    {selectedMandate?.id || "-"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: "#777",
                                        mb: 0.5,
                                    }}
                                >
                                    Payment Limit
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "15px",
                                        fontWeight: 600,
                                        color: "#262349",
                                    }}
                                >
                                    {selectedMandate?.mandate_limit !== null &&
                                        selectedMandate?.mandate_limit !== undefined
                                        ? `₹${selectedMandate.mandate_limit}`
                                        : "-"}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "#777",
                                    mb: 0.5,
                                }}
                            >
                                Status
                            </Typography>
                            <Typography
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: "12px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color:
                                        statusColors[
                                        selectedMandate?.mandate_status || ""
                                        ] || "#666",
                                    backgroundColor: "#fff",
                                }}
                            >
                                {selectedMandate?.mandate_status || "-"}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseCancelDialog}
                        disabled={cancelLoading}
                        sx={{
                            textTransform: "none",
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={
                            cancelLoading ? undefined : <CancelIcon />
                        }
                        onClick={handleConfirmCancel}
                        disabled={cancelLoading}
                        sx={{
                            textTransform: "none",
                        }}
                    >
                        {cancelLoading
                            ? "Cancelling..."
                            : "Confirm Cancel"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MandateDetails;
